import { useCallback, useEffect, useState } from "react";

import { Row, Col, Card, CardBody, CardTitle, Button } from "reactstrap";

// router
import { useLocation } from "react-router-dom";

// componentes
import PieChart from "../Dashboard/PieChart";
import FeedChart from "../Dashboard/FeedChart";
import DonutChart from "../Dashboard/DonutChart";
import AccessChart from "./AccessChart";
import ChartModal from "./ChartModal";
import Participants from "../Participant/Participants";
import Feed from "../Feed/Feed";
import RankingList from "./RankingList";

// utils
import {
  gameConfigs,
  interactionConfigs,
  ACCESS_CONFIG_CODE,
  ACCESS_TOP_LIMIT,
} from "./rankingConfig";

// services
import {
  getDashboardAccesses,
  getDashboardFeed,
  getClickButtonMenu,
} from "../../../utils/services/dashboard";
import { getTopRankingByConfiguration } from "../../../utils/services/ranking";

export default function GamesXSquad() {
  const {
    state: { idEvent: eventId },
  } = useLocation();

  // dados dos cards
  const [accesses, setAccesses] = useState({});
  const [feed, setFeed] = useState({});
  const [clickButtonMenu, setClickButtonMenu] = useState({});
  const [topAccesses, setTopAccesses] = useState([]);

  // gráfico ampliado
  const [accessChartModal, setAccessChartModal] = useState(false);

  // relatórios detalhados
  const [showAccessReport, setShowAccessReport] = useState(false);
  const [showFeedReport, setShowFeedReport] = useState(false);

  // volta dos relatórios detalhados para os cards
  const backToDashboard = () => {
    setShowAccessReport(false);
    setShowFeedReport(false);
  };

  // A api responde com chaves diferentes conforme o cache: `cachedData`
  // quando o Redis tem o valor, e o nome do dado (dashboardAccessesData,
  // dashboardFeedData) quando precisa calcular na hora. Ler só `cachedData`
  // deixaria os cards vazios na primeira visita, até o cache esquentar.
  function dashboardPayload(response, freshKey) {
    return response?.cachedData ?? response?.[freshKey] ?? {};
  }

  // busca os dados de acessos e feed do evento
  const getDashboardData = useCallback(() => {
    const reqParams = { eventId };

    getDashboardAccesses(reqParams)
      .then((response) => {
        setAccesses(dashboardPayload(response, "dashboardAccessesData"));
      })
      .catch((error) => {
        // console.log(error);
      });

    getDashboardFeed(reqParams)
      .then((response) => {
        setFeed(dashboardPayload(response, "dashboardFeedData"));
      })
      .catch((error) => {
        // console.log(error);
      });

    getClickButtonMenu(reqParams)
      .then((response) => {
        setClickButtonMenu(dashboardPayload(response, "analyticsButtonClick"));
      })
      .catch((error) => {
        // console.log(error);
      });

    // participantes que mais acessaram, contados pelas pontuações de LOGIN001.
    // Vem da api de ranking, que não passa pelo cache do dashboard: devolve
    // { data } direto, sem `count` — a lista é fechada em ACCESS_TOP_LIMIT
    getTopRankingByConfiguration({
      eventId,
      code: ACCESS_CONFIG_CODE,
      limit: ACCESS_TOP_LIMIT,
    })
      .then((response) => {
        setTopAccesses(response?.data ?? []);
      })
      .catch((error) => {
        setTopAccesses([]);
      });
  }, [eventId]);

  useEffect(() => {
    getDashboardData();
  }, [getDashboardData]);

  return (
    <>
      {showAccessReport && (
        <div>
          <div className="back-to-dashboard">
            <i onClick={backToDashboard} className="bx bx-arrow-back" />
            <span onClick={backToDashboard}>Games X-Squad</span>
          </div>
          <Participants idEvent={eventId} reportToDashboard={true} />
        </div>
      )}

      {showFeedReport && (
        <div>
          <div className="back-to-dashboard">
            <i onClick={backToDashboard} className="bx bx-arrow-back" />
            <span onClick={backToDashboard}>Games X-Squad</span>
          </div>
          <Feed idEvent={eventId} reportToDashboard={true} />
        </div>
      )}

      <ChartModal
        isOpen={accessChartModal}
        toggle={() => setAccessChartModal(!accessChartModal)}
        title="Quantidade de Acessos por Participante"
      >
        {/* mesmo gráfico do card, só mais alto: no modal a área visível é o
        dobro da largura, então entram mais colunas por vez sem mudar a leitura */}
        <AccessChart chartData={topAccesses} expanded />
      </ChartModal>

      {!showAccessReport && !showFeedReport && (
        <div>
          <CardTitle style={{ marginBottom: 16 }}>Games X-Squad</CardTitle>
          <Row>
            <Col xs="12" lg="6">
              <Card className="card-shadow">
                <CardBody>
                  <PieChart data={accesses} title="Acessos" />
                  <div className="dashboard-access-footer">
                    <span style={{ color: "black", fontWeight: 700 }}>
                      Total de Usuários cadastrados:{" "}
                      {accesses.totalparticipants}
                    </span>
                    <Button onClick={() => setShowAccessReport(true)}>
                      <i className="bx bxs-report" />
                      <span>Relatório Detalhado</span>
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xs="12" lg="6">
              <Card className="card-shadow">
                <CardBody>
                  <FeedChart feed={feed} />
                  <div className="dashboard-to-ranking">
                    <Button onClick={() => setShowFeedReport(true)}>
                      <i className="bx bxs-report" />
                      <span>Relatório Detalhado</span>
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* linha própria: em um terço da largura o card de Feed quebra —
          suas sub-colunas (rótulo / donut / quantidade) ficam estreitas
          demais e o conteúdo transborda a altura fixa do .rank-wrapper */}
          {/* h-100 nos dois: os gráficos têm alturas próprias diferentes
          (donut 384px, barras 350px), então sem isso o card de Games fica
          mais baixo que o de Cliques. O Col já estica, o Card é que parava
          na altura do próprio conteúdo.

          mb-0 + margem na Row: com h-100 o card mede 100% da altura do Col e
          o margin-bottom de 24px do tema passa a transbordar em vez de
          separar, colando a linha seguinte. A margem vai para a Row,
          mantendo os mesmos 24px ($grid-gutter-width) do resto da página */}
          <Row style={{ marginBottom: 24 }}>
            <Col xs="12" lg="6">
              <Card className="card-shadow h-100 mb-0">
                <CardBody>
                  <DonutChart
                    data={clickButtonMenu?.data}
                    title="Cliques do Menu"
                    style={{
                      zIndex: 1,
                    }}
                  />
                  <span style={{ color: "black", fontWeight: 700 }}>
                    Total de Cliques: {clickButtonMenu?.total}
                  </span>
                </CardBody>
              </Card>
            </Col>

            <Col xs="12" lg="6">
              <Card className="card-shadow h-100 mb-0">
                <CardBody>
                  <div className="rank-wrapper" style={{ height: "auto" }}>
                    {/* flex em vez de Row: o ícone precisa ficar na ponta
                    direita da mesma linha do título, e uma Col extra abriria
                    gutter entre os dois */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span className="title">
                        Quantidade de Acessos por Participante
                      </span>
                      <i
                        className="mdi mdi-arrow-expand-all"
                        title="Expandir gráfico"
                        style={{
                          color: "#04724c",
                          fontSize: 18,
                          cursor: "pointer",
                          // o ícone não cede largura para o título
                          flexShrink: 0,
                        }}
                        onClick={() => setAccessChartModal(true)}
                      />
                    </div>
                  </div>
                  <AccessChart chartData={topAccesses} />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xs="12">
              <Card className="card-shadow">
                <CardBody>
                  {/* height auto: o .rank-wrapper tem altura fixa de 367px,
                  dimensionada para os gráficos, e aqui só envolve o título —
                  sem isso sobra um vão grande antes da lista */}
                  <div
                    className="rank-wrapper"
                    style={{ height: "auto", marginBottom: 16 }}
                  >
                    <Row>
                      <span className="title">Ranking Games</span>
                    </Row>
                  </div>
                  <RankingList
                    selectConfigs={gameConfigs}
                    totalLabel="Ranking Games"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xs="12">
              <Card className="card-shadow">
                <CardBody>
                  <div
                    className="rank-wrapper"
                    style={{ height: "auto", marginBottom: 16 }}
                  >
                    <Row>
                      <span className="title">Ranking Interação</span>
                    </Row>
                  </div>
                  <RankingList
                    selectConfigs={interactionConfigs}
                    totalLabel="Ranking Interação"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
}
